<template>
  <div>
    <div v-if="showTools" class="row">
      <div v-if="custom_themes && custom_themes.length" class="col-12 mb-2">
        <multiselect
          v-model="selected_theme"
          :options="custom_themes"
          label="name"
          placeholder="Pick a theme"
          track-by="id"
          :allow-empty="true"
          :multiple="false"
          @select="themeChanged">
          <template #noResult>
            Search has no Results.
          </template>
        </multiselect>
      </div>
      <div class="col-12 d-flex">
        <ss-checkbox id="force-legacy" v-model="force_legacy" class="mr-2">
          Slim
        </ss-checkbox>
        <ss-checkbox v-if="isFeed" id="show-unpublished"
                     v-model="show_unpublished"
                     class="ml-2">
          Show Unpublished
        </ss-checkbox>
        <a href="/preferences#embed_themes" target="_blank" class="ml-auto"><span class="la la-plus" /> Add New Theme</a>
      </div>

      <div class="col-12">
        <hr>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <div class="d-flex flex-column justify-content-center align-items-center player-container">
          <div v-if="item.player_data" :class="{'player-root-legacy' : force_legacy, 'player-root-default' : !force_legacy, 'feed': isFeed}">
            <iframe ref="iframe"
                    seamless
                    :src="player_url"
                    width="100%"
                    :height="frame_height"
                    frameborder="0"
                    allow="clipboard-read; clipboard-write"
                    referrerpolicy="origin"
                    refererpolicy="origin" />
          </div>
        </div>
      </div>
    </div>

    <div v-if="showEmbedCode || showListenLink" class="row">
      <div class="col-12">
        <hr>
      </div>
    </div>

    <div v-if="showEmbedCode && $refs['iframe']" class="row">
      <div class="col-12">
        <h5 class="d-flex">
          Embed Code <small class="copy-text"><a href="javascript:;" class="kt-link" @click="copyValueToClipboard(embed_code)"><i class="la la-copy" />Copy </a></small>
        </h5>
        <textarea
          id="embed_code"
          class="embed-code"
          :value="embed_code"
          disabled="true" />
      </div>
    </div>
    <div v-if="showListenLink && $refs['iframe']" class="row">
      <div class="col-12">
        <h5 class="d-flex">
          Listen Link <small class="copy-text"><a href="javascript:;" class="kt-link" @click="copyValueToClipboard(player_url_no_preview)"><i class="la la-copy" />Copy </a></small>
        </h5>
        <input
          id="listen_link"
          type="text"
          class="embed-code"
          :value="listen_link"
          disabled="true">
      </div>
    </div>
    <div v-if="mixcloud_authorised && item.mixcloud_cloudcast && item.mixcloud_cloudcast.exists" v-perms="['mixcloud']" class="row">
      <div class="col-12">
        <h5>Mixcloud</h5>
        <iframe width="100%"
                frameBorder="0"
                seamless
                :src="`https://api.mixcloud.com${item.mixcloud_cloudcast.key}embed-html`">
          Browser not compatible!
        </iframe>
      </div>
    </div>
    <div v-if="mixcloud_authorised && item.mixcloud_cloudcast && item.mixcloud_cloudcast.status === 'retrying'">
      <div class="col-12">
        <h5>Mixcloud</h5>
        <div style="text-align: center; margin: auto;">
          <b>Due to Mixcloud API limitations, this Item couldn't be uploaded yet. Timbre will try again at:<b>
            <br><b><span style="font-size: 20px;">{{ retrying_at }}</span></b><br>
            <b>If your item is not uploaded after an hour then please unlink and try again.</b>
          </b></b>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

export default {
  props: {
    item: {
      required: true,
      type: Object,
    },
    parameterOverrides: {
      required: false,
      type: Object,
      default() {
        return {};
      },
    },
    layoutOverride: {
      type: String,
      required: false,
      default: null,
      validator: function (value) {
        return ['square', 'legacy'].indexOf(value) !== -1;
      },
    },
    showTools: {
      required: false,
      type: Boolean,
      default: false,
    },
    isLiveFeed: {
      required: false,
      type: Boolean,
      default: false,
    },
    isFeed: {
      required: false,
      type: Boolean,
      default: false,
    },
    showEmbedCode: {
      required: false,
      type: Boolean,
      default: true,
    },
    showListenLink: {
      required: false,
      type: Boolean,
      default: true,
    },
    fullWidth: {
      required: false,
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      custom_themes: [],
      selected_theme: null,
      force_legacy: false,
      force_no_theme: false,
      show_unpublished: true,
      mixcloud_authorised: window.mixcloud_authorised,
    };
  },
  computed: {
    iframe_classes() {
      let classes = '';
      if (this.isFeed) {
        classes += 'feed ';
      }

      if (this.layout === 'legacy') {
        classes += 'player-root-legacy ';
      }

      if (this.layout === 'square') {
        classes += 'player-root-default ';
      }

      if (this.fullWidth) {
        classes += 'w-100 ';
      }

      return classes;
    },

    retrying_at() {
      if (this?.item?.mixcloud_cloudcast?.retrying_at) {
        return moment(this.item.mixcloud_cloudcast.retrying_at).format('DD/MM/YYYY HH:mm:ss');
      }
      return null;
    },

    layout() {
      if (this.force_legacy) {
        return 'legacy';
      }
      if (this.layoutOverride) {
        return this.layoutOverride;
      }
      return 'square';
    },

    is_large_view() {
      return !this.isLiveFeed && (this.layout === 'square' || this.isFeed);
    },

    frame_height() {
      if (!this.is_large_view) return 150;
      else if (!this.isFeed) return 640;
      else return 700;
    },

    url_params() {
      const params = {
      };

      if (this.item.player_data.params) {
        // Create a non-reactive copy of the params
        Object.assign(params, JSON.parse(JSON.stringify(this.item.player_data.params)));
      }

      // Override params if provided to component
      if (this.parameterOverrides) {
        Object.assign(params, this.parameterOverrides);
      }

      if (this.selected_theme && !this.force_no_theme) {
        params.custom_theme = this.selected_theme.id;
      } else {
        delete params.custom_theme;
      }

      return {
        ...params,
        layout: this.layout,
      };
    },

    iframe_el() {
      return this.$refs['iframe'];
    },

    player_url() {
      const url = new URL(this.item.player_data.basic_url);
      for (const [key, val] of Object.entries(this.url_params)) {
        url.searchParams.append(key, val);
      }

      if (this.show_unpublished && !this.isLiveFeed) {
        if (this.isFeed) {
          return `${url}&skip=0&limit=10&preview=true`;
        }

        return `${url}&preview=true`;
      }

      return url;
    },

    player_url_no_preview() {
      const url = new URL(this.player_url);
      url.searchParams.delete('preview');
      return url.href;
    },

    listen_link() {
      const url = new URL(this.player_url);
      url.searchParams.delete('preview');
      return url.href;
    },

    embed_code() {
      const no_preview = this.$refs['iframe'].cloneNode();
      const url = new URL(this.player_url);
      url.searchParams.delete('preview');
      no_preview.src = url.href;

      return no_preview.outerHTML
        .replace('&amp;', '&')
        .replace(/data.+?\s/, '');
    },
  },
  watch: {
    selected_theme: {
      deep: true,
      handler() {
        this.updateForceLegacy();
      },
    },
  },
  async mounted() {
    if (!this.item.player_data) {
      console.error("This item does not have required player_data");
    } else {
      if (this.permissionsCanAccessFeature('embed_themes')) {
        await this.fetchSelectedTheme();
      }
      this.updateForceLegacy();
    }
  },
  methods: {
    updateForceLegacy() {
      this.force_legacy = this.isLiveFeed || !this.isFeed || this.selected_theme?.layout === 'legacy';
    },
    async fetchSelectedTheme() {
      try {
        const response = await axios.get(`/api/v1/custom-themes`);
        this.custom_themes = response.data.data.themes;
        const item_default_theme = this.item.player_data.params.custom_theme;
        // If we have already got a custom theme, pre-select it for the multiselect.
        const selected_theme = _.find(this.custom_themes, theme => theme.id == item_default_theme);

        if (selected_theme && !this.force_no_theme) {
          this.selected_theme = selected_theme;
        }
      } catch (error) {
        console.error(error);
        toastr.warning('Unable to retrieve your themes');
      }
    },
    themeChanged(theme) {
      if (!theme) {
        this.force_no_theme = true;
      }

      if (theme.layout === 'legacy') {
        this.force_legacy = true;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  .row-vertical-middle {
    display: flex;  /* make the row a flex container */
    align-items: center; /* vertically center each flex item in the container */
  }
  .embed-code {
    min-height: 20px;
    padding: 18px;
    background-color: #151515;
    border: 1px solid #030303;
    border-radius: 4px;
    -webkit-box-shadow: inset 0 1px 1px rgba(0,0,0,0.05);
    box-shadow: inset 0 1px 1px rgba(0,0,0,0.05);
    color:#FFFFFF;
    font-weight:bold;
    font-size:1.2rem;
    width:100%;
  }

  .copy-text {
    position:absolute;
    right:15px;
  }

  .player-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    max-height: 700px;

    .player-root-default {
      width: 100%;
    }

    .player-root-legacy {
      height: 150px;
      width: 100%;
      &.feed {
        height:700px;
      }
    }

  }
</style>
